/**
 * Skills Report Page
 *
 * Admin report showing riders ranked by computed Skills Score within each ride group.
 * - Group sections sorted by group_order ascending
 * - Riders ranked by skills_score descending, name ascending within each group
 * - Summary statistics, group filter, and CSV export
 * - Group readiness indicators per rider
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 4.1, 4.2, 4.3, 4.4, 5.1, 5.3, 5.4, 5.5,
 *              6.1, 6.2, 6.3, 6.4, 8.1, 8.2, 8.3, 9.1, 9.2, 9.3, 9.4,
 *              10.4, 10.5, 10.6, 11.2, 11.3, 11.5
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { restClient } from '../../api/rest-client';
import { Button } from '../../components/common/Button';
import { getZoneColor } from '../../utils/timeTrialUtils';
import type { TimeTrialZone } from '../../types/skills';
import type { SkillsReportGroup, SkillsReportResponse } from '../../types/skills';

/**
 * Compute summary statistics across all groups.
 * Exported for property-based testing (Property 8).
 */
export function computeSummaryStats(groups: SkillsReportGroup[]): {
  totalRiders: number;
  totalGroups: number;
  overallAverage: number;
} {
  const totalGroups = groups.length;
  let totalRiders = 0;
  let percentageSum = 0;

  for (const group of groups) {
    totalRiders += group.riders.length;
    for (const rider of group.riders) {
      percentageSum += rider.skills_percentage;
    }
  }

  const overallAverage = totalRiders > 0
    ? Math.round((percentageSum / totalRiders) * 10) / 10
    : 0;

  return { totalRiders, totalGroups, overallAverage };
}

/**
 * Compute group header statistics for a single group.
 * Exported for property-based testing (Property 6).
 */
export function computeGroupHeaderStats(group: SkillsReportGroup): {
  riderCount: number;
  avgPercentage: number;
  readinessSummary: Record<string, number>;
  zoneSummary: Record<string, number>;
} {
  const riderCount = group.riders.length;

  const avgPercentage = riderCount > 0
    ? Math.round(
        (group.riders.reduce((sum, r) => sum + r.skills_percentage, 0) / riderCount) * 10
      ) / 10
    : 0;

  const readinessSummary: Record<string, number> = {};

  for (const rider of group.riders) {
    const readiness = rider.group_readiness?.suggestion || 'Unknown';
    readinessSummary[readiness] = (readinessSummary[readiness] || 0) + 1;
  }

  const zoneSummary: Record<string, number> = {};
  for (const rider of group.riders) {
    const zone = rider.time_trial_zone?.zone ?? 'No Data';
    zoneSummary[zone] = (zoneSummary[zone] || 0) + 1;
  }

  return { riderCount, avgPercentage, readinessSummary, zoneSummary };
}

/**
 * Filter groups by selected group name.
 * Empty string means "All Groups".
 * Exported for property-based testing (Property 11).
 */
export function filterGroups(
  groups: SkillsReportGroup[],
  filter: string
): SkillsReportGroup[] {
  if (!filter) return groups;
  return groups.filter((g) => g.group_name === filter);
}

/**
 * Generate CSV content string from groups data and trigger download.
 * Exported for property-based testing (Property 9).
 *
 * Returns the CSV content string. Also triggers a browser download
 * when running in a browser environment.
 */
export function exportToCSV(groups: SkillsReportGroup[]): string {
  if (groups.length === 0 || groups.every((g) => g.riders.length === 0)) {
    alert('No data to export');
    return '';
  }

  const headers = [
    'Rank',
    'Name',
    'TT Zone',
    'Group',
    'Skills Mastered',
    'Total Skills',
    'Percentage',
    'Assessed Skills',
    'Total Skills',
    'Readiness',
  ];

  const rows: string[][] = [];

  // Groups are already sorted by group_order in the API response.
  // Within each group, riders are already sorted by score desc, name asc.
  for (const group of groups) {
    group.riders.forEach((rider, index) => {
      // Build TT Zone label for CSV
      const zoneLabel = rider.time_trial_zone?.zone ?? 'No Data';
      const crossGroupNote = rider.time_trial_zone?.zone === 'Ahead of Group+' && rider.time_trial_zone?.cross_group
        ? ` (Faster than ${rider.time_trial_zone.cross_group.riders_beaten} of ${rider.time_trial_zone.cross_group.higher_group_rider_count} in ${rider.time_trial_zone.cross_group.higher_group_name})`
        : '';
      const ttZoneCSV = zoneLabel + crossGroupNote;

      rows.push([
        String(index + 1),
        rider.name,
        ttZoneCSV,
        group.group_name,
        String(rider.skills_score),
        String(rider.max_possible_score),
        String(rider.skills_percentage),
        String(rider.assessed_count),
        String(rider.total_skills),
        rider.group_readiness.suggestion,
      ]);
    });
  }

  const csvContent = [
    headers.join(','),
    ...rows.map((r) => r.map((c) => `"${c}"`).join(',')),
  ].join('\n');

  // Trigger download in browser environment
  if (typeof document !== 'undefined') {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `skills-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return csvContent;
}


/**
 * Map a TimeTrialZone to Tailwind CSS classes for background and text color.
 */
function getZoneTailwindClasses(zone: TimeTrialZone | undefined): string {
  if (!zone) return 'bg-gray-100 text-gray-600';
  const color = getZoneColor(zone);
  switch (color) {
    case 'orange': return 'bg-orange-100 text-orange-800';
    case 'blue': return 'bg-blue-100 text-blue-800';
    case 'green': return 'bg-green-100 text-green-800';
    case 'dark green': return 'bg-green-200 text-green-900';
    case 'gray':
    default: return 'bg-gray-100 text-gray-600';
  }
}

/**
 * Format a record of category → count into a readable summary string.
 * e.g. { Improving: 3, Stable: 2 } → "3 Improving, 2 Stable"
 */
function formatSummary(counts: Record<string, number>): string {
  return Object.entries(counts)
    .filter(([, count]) => count > 0)
    .map(([label, count]) => `${count} ${label}`)
    .join(', ');
}

const SkillsReport: React.FC = () => {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState<SkillsReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupFilter, setGroupFilter] = useState<string>('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await restClient.getSkillsReport();
      setReportData(data);
    } catch {
      setError('Failed to load skills report data');
    } finally {
      setLoading(false);
    }
  };

  // Derived values + hooks MUST be computed before any early return so the hook
  // order is identical on every render (Rules of Hooks). Previously the useMemo
  // below sat after the loading/error early returns, so the loading->loaded
  // transition changed the hook count and threw React error #310.
  const groups = reportData?.groups ?? [];
  const displayedGroups = filterGroups(groups, groupFilter);
  const { totalRiders, totalGroups, overallAverage } = computeSummaryStats(groups);

  // Pre-compute group header stats once per render instead of inside JSX map
  const groupHeaderStatsMap = useMemo(() => {
    const map = new Map<string, ReturnType<typeof computeGroupHeaderStats>>();
    for (const group of displayedGroups) {
      map.set(group.group_name, computeGroupHeaderStats(group));
    }
    return map;
  }, [displayedGroups]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">Loading skills report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Skills Report</h1>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => navigate('/admin/reports')}
              aria-label="Back to Reports"
            >
              <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Reports
            </Button>
            <button
              onClick={() => exportToCSV(groups)}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              aria-label="Export CSV"
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* Summary Statistics */}
        <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6">
          <h2 className="text-lg font-semibold mb-3">Report Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <strong>Total Riders:</strong> {totalRiders}
            </div>
            <div>
              <strong>Total Groups:</strong> {totalGroups}
            </div>
            <div>
              <strong>Overall Average:</strong> {overallAverage}%
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="group-filter" className="block font-semibold mb-2">
                Filter by Group:
              </label>
              <select
                id="group-filter"
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
                aria-label="Filter by group"
              >
                <option value="">All Groups</option>
                {groups.map((group) => (
                  <option key={group.group_name} value={group.group_name}>
                    {group.group_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Group Sections */}
        {displayedGroups.length === 0 ? (
          <div className="text-center text-gray-500 py-8">No groups to display</div>
        ) : (
          displayedGroups.map((group) => {
            const { riderCount, avgPercentage, readinessSummary, zoneSummary } =
              groupHeaderStatsMap.get(group.group_name)!;

            return (
              <div key={group.group_name} className="mb-8">
                {/* Group Header */}
                <div className="bg-gray-100 border border-gray-300 rounded-t p-4">
                  <div className="flex flex-wrap justify-between items-start gap-2">
                    <div>
                      <h3 className="text-xl font-bold">{group.group_name}</h3>
                      <div className="text-sm text-gray-600 mt-1">
                        {riderCount} rider{riderCount !== 1 ? 's' : ''} · Avg {avgPercentage}%
                      </div>
                    </div>
                    <div className="text-sm text-right">
                      {formatSummary(readinessSummary) && (
                        <div className="text-gray-600">
                          <strong>Readiness:</strong> {formatSummary(readinessSummary)}
                        </div>
                      )}
                      <div className="text-gray-600">
                        <strong>TT Zones:</strong>{' '}
                        {Object.keys(zoneSummary).length === 0 ||
                         (Object.keys(zoneSummary).length === 1 && zoneSummary['No Data'])
                          ? 'No TT Data'
                          : formatSummary(zoneSummary)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rider Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse" aria-label={`${group.group_name} riders table`}>
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-4 py-2 text-left">Rank</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Name</th>
                        <th className="border border-gray-300 px-4 py-2 text-left" aria-label="Time Trial Zone">TT Zone</th>
                        <th className="border border-gray-300 px-4 py-2 text-right">Skills Mastered</th>
                        <th className="border border-gray-300 px-4 py-2 text-right">Total</th>
                        <th className="border border-gray-300 px-4 py-2 text-right">%</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Coverage</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Readiness</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.riders.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="border border-gray-300 px-4 py-2 text-center text-gray-500">
                            No riders in this group
                          </td>
                        </tr>
                      ) : (
                        group.riders.map((rider, index) => (
                          <tr key={rider.rider_id} className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-4 py-2">{index + 1}</td>
                            <td className="border border-gray-300 px-4 py-2">{rider.name}</td>
                            <td className="border border-gray-300 px-4 py-2"
                                aria-label={`Time Trial Zone: ${rider.time_trial_zone?.zone ?? 'No Data'}`}>
                              <span
                                className={`inline-block px-2 py-1 rounded text-xs font-medium ${getZoneTailwindClasses(rider.time_trial_zone?.zone)}`}
                                title={rider.time_trial_zone?.zone === 'Ahead of Group+' && rider.time_trial_zone?.cross_group
                                  ? `Faster than ${rider.time_trial_zone.cross_group.riders_beaten} of ${rider.time_trial_zone.cross_group.higher_group_rider_count} riders in ${rider.time_trial_zone.cross_group.higher_group_name}`
                                  : undefined}
                              >
                                {rider.time_trial_zone?.zone ?? 'No Data'}
                              </span>
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-right">{rider.skills_score}</td>
                            <td className="border border-gray-300 px-4 py-2 text-right">{rider.max_possible_score}</td>
                            <td className="border border-gray-300 px-4 py-2 text-right">{rider.skills_percentage}%</td>
                            <td className="border border-gray-300 px-4 py-2">
                              {rider.assessed_count} / {rider.total_skills}
                            </td>
                            <td className="border border-gray-300 px-4 py-2">{rider.group_readiness.suggestion}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SkillsReport;
